import React from 'react';
import { Tooltip } from '@material-ui/core';
import { IoMdEye } from 'react-icons/io';
import { RiFileSearchFill } from 'react-icons/ri';
import TableHeader from '../../../models/TableHeader';
import { formatCpfCnpj, getMoneyMask } from '../../../helpers';
import { toAmericanDate } from '../../../helpers/utils/utils';
import { CancelarEnvioIcon } from '../../../assets/Icons/CancelarEnvioIcon';
import { TableActionButton, TableActionContainer } from '../../../components/UI/Tabela/tableDefaultStyles';
import OrdenacaoColuna from '../../../components/UI/Tabela/Ordenacao/OrdenacaoColuna';

const SORT_FIELD_BY_COLUMN = {
  dataNota: 'dataNota',
  numeroNota: 'numeroNota',
  numeroNotaFiscalEletronica: 'numeroNotaFiscalEletronica',
};

export function buildNotasFiscaisPayload({
  filtrosRequest = {},
  periodoInicial = '',
  periodoFinal = '',
}) {
  return {
    ...filtrosRequest,
    statusNotaFiscal: filtrosRequest.statusNotaFiscal === 'todosStatus'
      ? null
      : filtrosRequest.statusNotaFiscal,
    tipoNotaFiscal: filtrosRequest.tipoNotaFiscal === 'todosTipos'
      ? 'todas'
      : filtrosRequest.tipoNotaFiscal,
    periodoInicial: toAmericanDate(periodoInicial),
    periodoFinal: toAmericanDate(periodoFinal),
  };
}

export function buildNotasFiscaisSort(columnOrdenacao = { element: 'dataNota', order: 'DESC' }) {
  const sortField = SORT_FIELD_BY_COLUMN[columnOrdenacao.element]
    || columnOrdenacao.element
    || 'dataNota';
  const sortOrder = columnOrdenacao.order || 'DESC';

  return `${sortField},${sortOrder}`;
}

export function buildNotasFiscaisPageable({
  page = 0,
  rowsPerPage = 10,
  columnOrdenacao = { element: 'dataNota', order: 'DESC' },
}) {
  return {
    page,
    size: rowsPerPage,
    sort: buildNotasFiscaisSort(columnOrdenacao),
  };
}

class TableNotasFiscaisFactory {
  makeCabecalhoTabela({
    arrowOrdenacao,
    columnOrdenacao,
    setArrowOrdenacao,
    setColumnOrdenacao,
  }) {
    const makeSortableLabel = (columnLabel, idColumn) => (
      <OrdenacaoColuna
        columnLabel={columnLabel}
        idColumn={idColumn}
        arrowOrdenacao={arrowOrdenacao}
        columnOrdenacao={columnOrdenacao}
        setArrowOrdenacaoFunc={setArrowOrdenacao}
        setColumnOrdenacao={setColumnOrdenacao}
      />
    );

    return [
      new TableHeader({
        id: 'dataNota',
        label: makeSortableLabel('Data Emissão', 'dataNota'),
        alignCell: 'center',
        maxWidth: 110,
      }),
      new TableHeader({
        id: 'numeroNota',
        label: makeSortableLabel('Nº RPS', 'numeroNota'),
        alignCell: 'center',
        maxWidth: 95,
      }),
      new TableHeader({
        id: 'numeroNotaFiscalEletronica',
        label: makeSortableLabel('Nº NFe', 'numeroNotaFiscalEletronica'),
        alignCell: 'center',
        maxWidth: 95,
      }),
      new TableHeader({
        id: 'nomeDestinatario',
        label: makeSortableLabel('Nome Destinatário', 'nomeDestinatario'),
        alignCell: 'flex-start',
        maxWidth: 180,
        showTip: true,
      }),
      new TableHeader({
        id: 'cnpj',
        label: makeSortableLabel('CNPJ', 'cnpj'),
        alignCell: 'center',
        maxWidth: 130,
      }),
      new TableHeader({
        id: 'valorAmigavel',
        label: makeSortableLabel('Honorários Amigáveis', 'valorAmigavel'),
        alignCell: 'flex-end',
        maxWidth: 120,
      }),
      new TableHeader({
        id: 'valorJudicial',
        label: makeSortableLabel('Honorários Judiciais', 'valorJudicial'),
        alignCell: 'flex-end',
        maxWidth: 120,
      }),
      new TableHeader({
        id: 'valorDespesa',
        label: makeSortableLabel('Despesas', 'valorDespesa'),
        alignCell: 'flex-end',
        maxWidth: 120,
      }),
      new TableHeader({
        id: 'statusNotaFiscal',
        label: makeSortableLabel('Status', 'statusNotaFiscal'),
        alignCell: 'center',
        maxWidth: 170,
      }),
      new TableHeader({
        id: 'tipoEmissao',
        label: makeSortableLabel('Tipo Emissão', 'tipoEmissao'),
        alignCell: 'center',
        maxWidth: 120,
      }),
      new TableHeader({
        id: 'acoes',
        label: 'Ações',
        alignCell: 'center',
        maxWidth: 85,
      }),
    ];
  }

  makeConteudoTabela(
    notasFiscais,
    permissoes,
    handleCancelNotaFiscais,
    visualizarNotaFiscalCliente,
    downloadProtocoloCancelamentoNF,
  ) {
    if (!notasFiscais || notasFiscais.length === 0) {
      return [];
    }

    return notasFiscais.map((nota) => {
      const notaId = nota?.idNotaFiscalCliente;
      const status = nota?.statusNotaFiscal || '';
      const hasNumeroNfe = Boolean(String(nota?.numeroNotaFiscalEletronica || '').trim());
      const hasCodigoVerificacao = Boolean(String(nota?.codigoVerificacao || '').trim());
      const isAtiva = status === 'Ativa';
      const isCancelada = status === 'Cancelada' || status === 'Cancelada fora do prazo';
      const disableCancelar = !permissoes?.prestarContas || !isAtiva;
      const disableVisualizar = !hasNumeroNfe || !hasCodigoVerificacao;
      const disableProtocolo = !(isCancelada && hasNumeroNfe && hasCodigoVerificacao);

      return {
        id: nota?.idNotaFiscalCliente,
        values: {
          dataNota: nota?.dataNota || '',
          numeroNota: nota?.numeroNota || '',
          numeroNotaFiscalEletronica: nota?.numeroNotaFiscalEletronica || '',
          nomeDestinatario: nota?.nomeDestinatario || '',
          cnpj: formatCpfCnpj(nota?.cnpj),
          valorAmigavel: getMoneyMask(nota?.valorAmigavel),
          valorJudicial: getMoneyMask(nota?.valorJudicial),
          valorDespesa: getMoneyMask(nota?.valorDespesa),
          statusNotaFiscal: nota?.statusNotaFiscal || '',
          tipoEmissao: nota?.tipoEmissao || '',
          acoes: (
            <div style={{ display: 'flex' }}>
              <TableActionContainer>
                <Tooltip title="Visualizar Nota Fiscal" placement="bottom">
                  <TableActionButton
                    disabled={disableVisualizar}
                    onClick={() => visualizarNotaFiscalCliente(nota.urlPrefeituraNF)}
                  >
                    <IoMdEye size={20} color={disableVisualizar ? 'lightGrey' : 'blackTwo'} />
                  </TableActionButton>
                </Tooltip>
              </TableActionContainer>
              <TableActionContainer>
                <Tooltip title="Cancelar Nota Fiscal" placement="bottom">
                  <TableActionButton
                    disabled={disableCancelar}
                    onClick={() => handleCancelNotaFiscais(notaId)}
                  >
                    <CancelarEnvioIcon size={14} color={disableCancelar ? 'lightGrey' : 'blackTwo'} />
                  </TableActionButton>
                </Tooltip>
              </TableActionContainer>
              <TableActionContainer>
                <Tooltip title="Download Protocolo de Cancelamento" placement="bottom">
                  <TableActionButton
                    disabled={disableProtocolo}
                    onClick={() => downloadProtocoloCancelamentoNF(notaId)}
                  >
                    <RiFileSearchFill size={20} color={disableProtocolo ? 'lightGrey' : 'blackTwo'} />
                  </TableActionButton>
                </Tooltip>
              </TableActionContainer>
            </div>
          ),
          codigoVerificacao: nota?.codigoVerificacao || '',
          nomeUsuarioEmissao: nota?.nomeUsuarioEmissao || '',
          nomePrefeitura: nota?.nomePrefeitura || '',
          valorLiquido: getMoneyMask(nota?.valorLiquido),
          valorRetencaoPIS: getMoneyMask(nota?.valorRetencaoPIS),
          valorRetencaoCOFINS: getMoneyMask(nota?.valorRetencaoCOFINS),
          valorRetencaoCSLL: getMoneyMask(nota?.valorRetencaoCSLL),
          valorRetencaoIR: getMoneyMask(nota?.valorRetencaoIR),
        },
      };
    });
  }
}

export const tableNotasFiscaisFactory = new TableNotasFiscaisFactory();
